import { useState, useCallback, useRef, useMemo } from 'react';
import { useBodyProcessor } from '../workers';

const CHUNK_SIZE = 100 * 1024;
const INITIAL_LINES = 500;
const LOAD_MORE_LINES = 500;

type StreamingBodyState = {
  lines: string[];
  displayedLineCount: number;
  totalSize: number;
  loadedBytes: number;
  isLoading: boolean;
  isFullyFetched: boolean;
  error: string | null;
};

export type StreamingBodyResult = {
  displayedContent: string;
  hasMoreLines: boolean;
  loadMoreLines: () => void;
  startStreaming: () => Promise<void>;
  isLoading: boolean;
  isFullyFetched: boolean;
  displayedLineCount: number;
  totalLineCount: number;
  loadProgress: number;
  error: string | null;
  reset: () => void;
};

export function useStreamingBody(
  bodyRef: string | undefined,
): StreamingBodyResult {
  const [state, setState] = useState<StreamingBodyState>({
    lines: [],
    displayedLineCount: INITIAL_LINES,
    totalSize: 0,
    loadedBytes: 0,
    isLoading: false,
    isFullyFetched: false,
    error: null,
  });

  const partialLineRef = useRef('');
  const streamingRef = useRef(false);
  const { processChunk: processChunkWithWorker } = useBodyProcessor();

  const reset = useCallback(() => {
    setState({
      lines: [],
      displayedLineCount: INITIAL_LINES,
      totalSize: 0,
      loadedBytes: 0,
      isLoading: false,
      isFullyFetched: false,
      error: null,
    });
    partialLineRef.current = '';
    streamingRef.current = false;
  }, []);

  const fetchChunk = useCallback(
    async (
      offset: number,
    ): Promise<{
      data: string;
      bytesRead: number;
      totalSize: number;
      hasMore: boolean;
    } | null> => {
      if (!bodyRef || !window.electronAPI?.bodyStorage?.getChunked) {
        return null;
      }

      try {
        const result = await window.electronAPI.bodyStorage.getChunked(
          bodyRef,
          offset,
          CHUNK_SIZE,
        );

        if (!result.success || result.data === undefined) {
          throw new Error(result.error || 'Failed to fetch chunk');
        }

        return {
          data: result.data,
          bytesRead: result.bytesRead ?? 0,
          totalSize: result.totalSize ?? 0,
          hasMore: result.hasMore ?? false,
        };
      } catch (err) {
        throw new Error(`Chunk fetch failed: ${err}`);
      }
    },
    [bodyRef],
  );

  const processChunk = useCallback(
    async (chunk: string, isLastChunk: boolean): Promise<string[]> => {
      // Use worker for processing (automatically falls back to sync for small chunks)
      const result = await processChunkWithWorker({
        chunk,
        isLastChunk,
        existingPartial: partialLineRef.current,
      });

      partialLineRef.current = result.partial;
      return result.lines;
    },
    [processChunkWithWorker],
  );

  const startStreaming = useCallback(async () => {
    if (!bodyRef || streamingRef.current) return;

    streamingRef.current = true;
    partialLineRef.current = '';

    setState((prev) => ({
      ...prev,
      isLoading: true,
      error: null,
      lines: [],
      loadedBytes: 0,
      isFullyFetched: false,
    }));

    let offset = 0;
    const allLines: string[] = [];

    try {
      let hasMore = true;
      while (hasMore) {
        const chunk = await fetchChunk(offset);
        if (!chunk) {
          throw new Error('Failed to fetch chunk');
        }

        const newLines = await processChunk(chunk.data, !chunk.hasMore);
        allLines.push(...newLines);
        offset += chunk.bytesRead;
        hasMore = chunk.hasMore;

        const snapshot = [...allLines];
        const loadedBytes = offset;
        const fullyFetched = !hasMore;
        setState((prev) => ({
          ...prev,
          lines: snapshot,
          loadedBytes,
          totalSize: chunk.totalSize,
          isFullyFetched: fullyFetched,
        }));

        // Yield to main thread for UI updates
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      setState((prev) => ({
        ...prev,
        isLoading: false,
        isFullyFetched: true,
      }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: String(err),
      }));
    } finally {
      streamingRef.current = false;
    }
  }, [bodyRef, fetchChunk, processChunk]);

  const loadMoreLines = useCallback(() => {
    setState((prev) => ({
      ...prev,
      displayedLineCount: prev.displayedLineCount + LOAD_MORE_LINES,
    }));
  }, []);

  const displayedContent = useMemo(
    () => state.lines.slice(0, state.displayedLineCount).join('\n'),
    [state.lines, state.displayedLineCount],
  );
  const hasMoreLines = state.displayedLineCount < state.lines.length;
  const totalLineCount = state.lines.length;
  const loadProgress =
    state.totalSize > 0 ? state.loadedBytes / state.totalSize : 0;

  return {
    displayedContent,
    hasMoreLines,
    loadMoreLines,
    startStreaming,
    isLoading: state.isLoading,
    isFullyFetched: state.isFullyFetched,
    displayedLineCount: Math.min(state.displayedLineCount, state.lines.length),
    totalLineCount,
    loadProgress,
    error: state.error,
    reset,
  };
}
